import { NgClass } from "@angular/common";
import {
    AfterViewInit,
    Component,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { FormsModule, ReactiveFormsModule, UntypedFormGroup } from "@angular/forms";
import { CardComponent } from "@components/cards/card/card.component";
import { InputComponent } from "@components/input/input.component";
import { SelectorComponent } from "@components/selector/selector.component";
import { SimpleSelectorComponent } from "@components/simple-selector/simple-selector.component";
import {
    Observable,
    Subject,
    Subscription,
    of,
    timer
} from "rxjs";
import { catchError, map, takeUntil } from "rxjs/operators";
import { ModalSettings } from "~app/common/components/modal/modal-models";
import { RaskModalService } from "~app/common/components/modal/rask-modal.service";
import { IRadiobuttonItem } from "~app/common/components/radiobuttongroup/radiobutton/IRadiobuttonItem";
import { Arbetsordertyper } from "~app/common/models/constants";
import { FormBinderService } from "~app/common/services/form-binder.service";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { IValidationScope } from "~app/common/validation/IValidationScope";
import { ValidationContext } from "~app/common/validation/validationContext";
import { ValidationScopes } from "~app/common/validation/ValidationScopes";
import { ArbetsorderAtgardskedjaBehaviour } from "~app/features/planera/behaviours/ArbetsorderAtgardskedjaBehaviour";
import { UpdateAtgardAvslutaArbetsorderRvCommand } from "~app/features/planera/commands/UpdateAtgardAvslutaArbetsorderRvCommand";
import { IArbetsorderDetail } from "~app/features/planera/model/IArbetsorderDetail";
import { IAtgard } from "~app/features/planera/model/IAtgard";
import { ArbetsorderMediatorService } from "~app/features/planera/services/arbetsorder-mediator.service";
import { ArbetsorderAtgardService } from "../../arbetsorder-atgard.service";
import { ArbetsorderAtgardProduktionAvslutadRvValidation } from "./arbetsorder-atgard-produktion-avslutad-rv.validation";

@Component({
    selector: "rask-arbetsorder-atgard-produktion-avslutad-rv",
    templateUrl: "./arbetsorder-atgard-produktion-avslutad-rv.component.html",
    styleUrls: ["./arbetsorder-atgard-produktion-avslutad-rv.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        FormsModule,
        ReactiveFormsModule,
        CardComponent,
        NgClass,
        InputComponent,
        SelectorComponent,
        SimpleSelectorComponent
    ]
})
export class ArbetsorderAtgardProduktionAvslutadRvComponent implements OnInit, OnDestroy, OnChanges, AfterViewInit {
    readonly atgardService = inject(ArbetsorderAtgardService);

    @Input() arbetsorder: IArbetsorderDetail;
    @Input() atgard: IAtgard;
    form: UntypedFormGroup;
    hasSchemalagdRelateradGrotArbetsorder = false;
    optionsAtgardUtford: string[] = [
        "Ja",
        "Nej"
    ];
    optionsGrotanpassad = [
        {
            key: true,
            value: "Ja"
        },
        {
            key: false,
            value: "Nej"
        }
    ] as IRadiobuttonItem[];
    @Input() rootForm: UntypedFormGroup;
    validation: IValidationScope;
    validationContext: ValidationContext;
    private readonly arbetsorderMediatorService = inject(ArbetsorderMediatorService);
    private readonly formBinder = inject(FormBinderService);
    private readonly raskModalService = inject(RaskModalService);
    private readonly unsubscribe$: Subject<void>;

    private formSubscription: Subscription;

    constructor() {
        this.unsubscribe$ = new Subject<void>();
    }
    ngAfterViewInit(): void {
        this.validationContext.validate();
    }

    atgardUtfordViewFormatter = (item) => {
        if (item === undefined) {
            return undefined;
        }
        return item === "Ja" ? true : false;
    };

    ngOnChanges(changes: SimpleChanges) {
        if (changes.arbetsorder && this.validationContext) {
            this.validationContext.validate();
        }
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    ngOnInit(): void {
        this.setUpForm();
    }

    onBeforeChangeGrotanpassad = ($event: boolean): Observable<boolean> => {
        if (!this.hasSchemalagdRelateradGrotArbetsorder || $event) {
            return of(true);
        }
        const confirm = this.raskModalService.confirm(new ModalSettings("Avsluta arbetsorder",
            `Relaterad grotarbetsorder är schemalagd. Vill du ange att åtgärden inte är Grotanpassad ändå?
             Åtgärden kommer då tas bort från grotarbetsordern i samband med att du avslutar arbetsordern.`));
        return confirm
            .pipe(map(x => x === "save" ? true : false))
            .pipe(catchError(() => of(false)));
    };

    private addUpdateCommand() {
        this.arbetsorder.commandManager.addOrUpdate(new UpdateAtgardAvslutaArbetsorderRvCommand(c => {
            c.atagardId = this.atgard.id;
            c.atgardUtford = this.atgard.atgardUtford;
            c.grotAnpassad = this.atgard.grotAnpassad;
            c.grotvolym = this.atgard.grotVolym;
            c.kvalitetssakradGrotvolym = this.atgard.kvalitetssakradGrotvolym;
            c.arManuellGrotvolym = this.atgard.arManuellGrotvolym;
        }), command => command.atagardId === this.atgard.id);
    }

    private setGrotvolymFromStina(grotvolym: number, manuell: boolean): void {
        if (!this.atgard.grotuttagProduktionsplanerat) {
            return;
        }

        this.atgard.kvalitetssakradGrotvolym = grotvolym;
        this.form.get("kvalitetssakradGrotvolym").setValue(grotvolym);

        this.atgard.arManuellGrotvolym = manuell;
        this.form.get("arManuellGrotvolym").setValue(manuell);

        this.atgard.grotVolym = grotvolym;
        this.form.get("grotVolym").setValue(grotvolym);

        const isGrotanpassad = grotvolym > 0;
        this.atgard.grotAnpassad = isGrotanpassad;
        this.form.get("grotAnpassad").setValue(isGrotanpassad);
    }

    private setUpForm() {
        const atgardskedjeKopplingar = new ArbetsorderAtgardskedjaBehaviour(this.arbetsorder).getRelateradeAtgardskopplingar(this.atgard.id);

        if (!ObjectUtils.isNullOrUndefined(atgardskedjeKopplingar)) {
            this.hasSchemalagdRelateradGrotArbetsorder = atgardskedjeKopplingar.some((x) =>
                x.arbetsorderTyp === Arbetsordertyper.Grotskotning &&
                x.isSchemalagd
            );
        }

        this.form = this.formBinder.CreateGroup(this.atgard);
        if (!this.atgard.atgardUtford) {
            this.form.get("grotAnpassad").disable();
            this.form.get("grotVolym").disable();
        }

        this.validationContext = ArbetsorderAtgardProduktionAvslutadRvValidation.init(this.atgard);
        this.arbetsorderMediatorService.addValidationContext(this.validationContext);
        this.validationContext.scope(ValidationScopes.spara).subscribe(v => {
            this.validation = v;
        });

        this.form.get("grotAnpassad").valueChanges
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(newVal => {
                this.form.get("grotAnpassad").updateValueAndValidity({
                    onlySelf: true,
                    emitEvent: false
                });
                this.triggetGrotvolymValidation(this.atgard.grotAnpassad !== newVal);
                this.atgard.grotAnpassad = newVal;
            });

        this.form.get("atgardUtford").valueChanges
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((newVal: boolean | undefined) => {
                if (newVal === false) {
                    // Om utryckligen satt till false så ska grotAnpassad och grotVolym nollställas
                    this.form.get("grotAnpassad").setValue(null);
                    this.form.get("grotVolym").setValue(null);
                }

                if (newVal) {
                    this.form.get("grotAnpassad").enable();
                    this.form.get("grotVolym").enable();
                } else {
                    this.form.get("grotAnpassad").disable();
                    this.form.get("grotVolym").disable();
                }

                this.triggetGrotvolymValidation(this.atgard.atgardUtford !== newVal);
            });

        this.formSubscription?.unsubscribe();
        this.formSubscription = this.form.valueChanges.pipe(
            takeUntil(this.unsubscribe$)
        ).subscribe(x => {
            this.addUpdateCommand();
        });

        this.formBinder.attachToRootForm(this.rootForm, this.form, `atgar${this.atgard.id}`)
            .subscribe(x => {
                this.formBinder.attachValidation(this.validationContext.current, this.form, true);
                this.validationContext.validate();

                timer(0).subscribe(() => {
                    this.rootForm.updateValueAndValidity({
                        onlySelf: false,
                        emitEvent: true
                    });
                });
            });

        this.atgardService.grotvolymer$
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((result) => {
                const grotvolym = result?.volymer?.find(x => x.atgardId === this.atgard.id);
                if (ObjectUtils.isNullOrUndefined(grotvolym)) {
                    return;
                }

                this.setGrotvolymFromStina(grotvolym.kvalitetssakradGrotvolym, grotvolym.arManuellGrotvolym);
            });
    }

    private triggetGrotvolymValidation(valueHasChanged: boolean) {
        timer(0).subscribe(() => {
            this.validationContext.current.rules.forEach(rule => {
                rule.validateModel();
            });
            if (valueHasChanged) {
                this.form.get("grotVolym").updateValueAndValidity({
                    onlySelf: false,
                    emitEvent: true
                });
            }
        });
    }
}
